"use client";

import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { adminLogin, clearAdminKey, getAdminKey } from "@/lib/api";

type AdminUser = { email: string };

type AdminAuthContextValue = {
  user: AdminUser | null;
  ready: boolean;
  login: (apiKey: string) => Promise<void>;
  logout: () => void;
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    const apiKey = getAdminKey();
    if (apiKey) {
      setUser({ email: "admin@gameit.internal" });
    }
    setReady(true);
  }, []);

  const login = useCallback(async (apiKey: string) => {
    const result = await adminLogin(apiKey);
    if (!result.success) {
      throw new Error(result.error || "Login failed");
    }
    setUser({ email: "admin@gameit.internal" });
    router.push("/");
    router.refresh();
  }, [router]);

  const logout = useCallback(() => {
    clearAdminKey();
    setUser(null);
    router.push("/login");
    router.refresh();
  }, [router]);

  const value = useMemo(
    () => ({ user, ready, login, logout }),
    [user, ready, login, logout]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
}
